import { createGlobalStyle, css } from 'styled-components';

import { THEME_NAME } from '../constants/theme';

import DarkTheme from './themes/dark';
import LightTheme from './themes/light';

function getThemeStyles({ theme }) {
  switch (theme.name) {
    case THEME_NAME.DARK: {
      return DarkTheme;
    }
    case THEME_NAME.LIGHT: {
      return LightTheme;
    }

    default: {
      return LightTheme;
    }
  }
}

const GlobalStyle = createGlobalStyle`
  :root {
    ${getThemeStyles}

    ${({ theme }) => !theme.redesign && css`
      @property --color-ai-gradient-start {
        syntax: '<color>';
        initial-value: rgb(39, 219, 22);
        inherits: false;
      }

      @property --color-ai-gradient-end {
        syntax: '<color>';
        initial-value: rgb(18, 198, 2);
        inherits: false;
      }
    `}
  }

  * {
    box-sizing: border-box;
    outline: none;
  }

  &:after,
  &:before {
    box-sizing: border-box;
  }

  html {
    position: relative;
    background-color: var(${({ theme }) => theme.redesign ? '--color-background-dark' : '--color-background-light'});
    height: 100%;
  }

  body {
    height: 100%;
    margin: 0;
    padding: 0;
    background-color: inherit;
    color: var(--color-text);
    font-family: var(--font-vela-sans);
    font-size: 13px;
    line-height: 18px;

    &:not(.sb-show-main) {
      overflow: hidden;
    }
  }

  a,
  p,
  th,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  div,
  span,
  button,
  strong {
    font-weight: var(--font-weight-regular);
  }

  #app,
  #app div[data-reactroot] {
    height: 100%;
  }

  #app {
    position: relative;
    background-color: inherit;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  p {
    margin: 0 0 1em 0;
  }

  img {
    display: block;
  }

  .error {
    color: var(--color-rouge);
  }

  .info {
    color: var(--color-clover);
  }

  .w-100 {
    width: 100%;
  }

  .w-66 {
    width: 66.666%;
  }

  .w-50 {
    width: 50%;
  }

  .w-33 {
    width: 33.333%;
  }

  .h-100 {
    height: 100%;
  }

  .d-flex {
    display: flex;
  }

  .flex-wrap {
    flex-wrap: wrap;
  }

  .d-none {
    display: none;
  }

  .disable-pointer-events {
    pointer-events: none;
  }

  .enable-pointer-events {
    pointer-events: auto;
  }

  .uppercase {
    text-transform: uppercase;
  }

  .cursor-auto {
    cursor: auto !important;
  }

  .cursor-default {
    cursor: default !important;
  }

  .cursor-pointer {
    cursor: pointer !important;
  }

  .cursor-text {
    cursor: text !important;
  }

  .cursor-wait {
    cursor: wait !important;
  }

  .relative {
    position: relative;
  }

  .flex-direction-column {
    flex-direction: column;
  }

  .flex-direction-row-reverse {
    flex-direction: row-reverse;
  }

  .justify-content-center {
    justify-content: center;
  }

  .justify-content-space-between {
    justify-content: space-between;
  }

  .justify-content-start {
    justify-content: flex-start;
  }

  .justify-content-end {
    justify-content: flex-end;
  }

  .align-items-center {
    align-items: center;
  }

  .align-items-start {
    align-items: start;
  }

  .align-self-center {
    align-self: center;
  }

  .flex-all-center {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .flex-auto-size {
    flex: 1 1 auto;
  }

  .flex-fixed-size {
    flex: 1 0 auto;
  }

  .flex-grow-1 {
    flex-grow: 1;
  }

  .text-right {
    text-align: right;
  }

  .text-center {
    text-align: center;
  }

  .text-left {
    text-align: left;
  }

  .overflow-hidden {
    overflow: hidden;
  }

  .no-overflow {
    overflow: unset !important;
  }

  .font-light {
    font-weight: var(--font-weight-light);
  }

  .font-regular {
    font-weight: var(--font-weight-regular);
  }

  .font-medium {
    font-weight: var(--font-weight-medium);
  }

  .font-bold {
    font-weight: var(--font-weight-bold);
  }

  .font-semi-bold {
    font-weight: var(--font-weight-semi-bold);
  }

  .react-datepicker__aria-live {
    display: none;
  }

  .react-datepicker-popper {
    height: max-content;

    & > div,
    & > div > div {
      width: inherit;
      max-height: inherit;
    }
  }
`;

export default GlobalStyle;
